import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGasI18n } from '../../shared/kit';
import { fetchPublicPlans } from './api';
import type { PlanCard, PlansListInitData } from './types';

interface Props {
    initData: PlansListInitData;
}

function formatPrice(price: number | null, locale: string): string | null {
    if (price === null || price === undefined) {
        return null;
    }
    return new Intl.NumberFormat(locale === 'en' ? 'en-CH' : 'fr-CH', {
        style: 'currency',
        currency: 'CHF',
    }).format(price);
}

function formatDate(date: string | null | undefined, locale: string): string | null {
    if (!date) {
        return null;
    }
    const parsed = new Date(date.replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }
    return new Intl.DateTimeFormat(locale === 'en' ? 'en-CH' : 'fr-CH', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(parsed);
}

function PlanCardView({ plan, locale }: { plan: PlanCard; locale: string }): React.ReactElement {
    const { t } = useGasI18n();
    const priceLabel = formatPrice(plan.price, locale);
    const periodLabel = t(`membership.plan.period_kinds.${plan.period_kind}`);
    const benefits = Array.isArray(plan.benefits) ? plan.benefits.filter(Boolean) : [];
    const intro = plan.intro || plan.description_excerpt || t('membership.index.default_intro');
    const expiryLabel = formatDate(plan.current_valid_until, locale);
    const isCurrent = Boolean(plan.is_current);
    const canRenew = Boolean(plan.can_renew && plan.buy_url);

    return (
        <article className="gas-membership-plans-list__card">
            <a className={`gas-membership-plans-list__card-media${plan.image_url ? '' : ' gas-membership-plans-list__card-media--fallback'}`} href={plan.plan_url}>
                {plan.image_url ? (
                    <img
                        className="gas-membership-plans-list__card-image"
                        src={plan.image_url}
                        alt={plan.name}
                        loading="lazy"
                    />
                ) : (
                    <span className="gas-membership-plans-list__card-monogram" aria-hidden="true">
                        {plan.name.slice(0, 2).toUpperCase()}
                    </span>
                )}
            </a>

            <header className="gas-membership-plans-list__card-header">
                <span className="gas-membership-plans-list__eyebrow">{t('membership.index.card_eyebrow')}</span>
                <h2 className="gas-membership-plans-list__card-title">
                    <a className="gas-membership-plans-list__card-link" href={plan.plan_url}>
                        {plan.name}
                    </a>
                </h2>
                <p className="gas-membership-plans-list__card-meta">
                    {priceLabel && <span className="gas-membership-plans-list__price">{priceLabel}</span>}
                    <span className="gas-membership-plans-list__period">{periodLabel}</span>
                    {plan.is_recurring && (
                        <span className="gas-membership-plans-list__badge">{t('membership.plan.recurring')}</span>
                    )}
                </p>
            </header>

            <p className="gas-membership-plans-list__card-description">{intro}</p>

            {benefits.length > 0 ? (
                <ul className="gas-membership-plans-list__features">
                    {benefits.slice(0, 5).map((benefit) => (
                        <li key={benefit} className="gas-membership-plans-list__feature">
                            {benefit}
                        </li>
                    ))}
                </ul>
            ) : plan.capabilities.length > 0 && (
                <div className="gas-membership-plans-list__badges">
                    {plan.capabilities.map((cap) => (
                        <span key={cap.key} className="gas-membership-plans-list__badge">{t(cap.label_key)}</span>
                    ))}
                </div>
            )}

            {plan.requires_cotisation && (
                <p className="gas-membership-plans-list__notice">{t('membership.plan.requires_cotisation')}</p>
            )}

            {isCurrent && (
                <p className="gas-membership-plans-list__notice gas-membership-plans-list__notice--active">
                    {expiryLabel
                        ? t('membership.index.already_active_until', { date: expiryLabel })
                        : t('membership.index.already_active')}
                </p>
            )}

            <div className="gas-membership-plans-list__card-actions">
                {canRenew ? (
                    <a className="gas-button gas-button--primary" href={plan.buy_url}>
                        {t('membership.index.renew')}
                    </a>
                ) : isCurrent ? (
                    <a className="gas-button gas-button--secondary" href={plan.manage_url || plan.plan_url}>
                        {t('membership.index.manage_membership')}
                    </a>
                ) : plan.is_purchasable && plan.buy_url ? (
                    <a className="gas-button gas-button--primary" href={plan.buy_url}>
                        {t('membership.index.subscribe')}
                    </a>
                ) : null}
                <a className="gas-button gas-button--outline" href={plan.plan_url}>
                    {t('membership.index.details')}
                </a>
            </div>
        </article>
    );
}

export default function App({ initData }: Props): React.ReactElement {
    const { t } = useGasI18n();

    const { data: plans } = useQuery({
        queryKey: ['membership-public-plans', initData.locale],
        queryFn: () => fetchPublicPlans(initData),
        initialData: initData.plans,
        staleTime: 5 * 60 * 1000,
    });

    const list = plans ?? initData.plans;

    return (
        <section className="gas-membership-plans-list">
            <header className="gas-membership-plans-list__header">
                <span className="gas-membership-plans-list__header-kicker">{t('membership.index.kicker')}</span>
                <h1 className="gas-membership-plans-list__title">{t('membership.index.title')}</h1>
                <p className="gas-membership-plans-list__subtitle">{t('membership.index.subtitle')}</p>
            </header>

            {list.length === 0 ? (
                <p className="gas-membership-plans-list__empty">{t('membership.index.empty')}</p>
            ) : (
                <div className="gas-membership-plans-list__grid">
                    {list.map((plan) => (
                        <PlanCardView key={plan.id} plan={plan} locale={initData.locale} />
                    ))}
                </div>
            )}

            {initData.advisor_url && (
                <div className="gas-membership-plans-list__advisor">
                    <a className="gas-button gas-button--secondary" href={initData.advisor_url}>
                        {t('membership.index.advisor_cta')}
                    </a>
                </div>
            )}
        </section>
    );
}
